import styles from '../EmployerPortalJobs/EmployerPortalJobs.module.css';
import candidatesStyles from './EmployerPortalCandidates.module.css';
import { TbDotsVertical } from 'react-icons/tb';
import { FaPlus } from 'react-icons/fa';
import userAvatarPlaceholder from '../../../../assets/images/other/user_avatar_placeholder.png';
import { Typography } from '../../../Typography/typography';
import { VerifyGreenBadge } from '../../../../assets/images/icons';
import { t } from 'i18next';
import { getTimeAgo } from '../../../../utils/formatHelpers';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import BasicPopup from '../../../UI/Popups/BasicPopup/BasicPopup';
import CreateFavListForm from '../FavTalents/CreateFavList/CreateFavListForm';

// Helper function to get last active time
const getLastActiveTime = (last_visit) => {
    const date_info = getTimeAgo(new Date(last_visit));
    const { unit_plural, count } = date_info;

    if (['month', 'months', 'year', 'years'].includes(unit_plural)) return null;

    if (['second', 'seconds', 'minute'].includes(unit_plural) || (unit_plural === 'minutes' && count < 5)) {
        return t('general.UI.online');
    } else {
        return `${count} ${t('general.UI.' + unit_plural)} ${t('general.UI.ago')}`;
    }
};

// Component for rendering profile information
const ProfileInfo = ({ item }) => {
    const truncateName = (name) => {
        return name.length > 20 ? name.substring(0, 20) + "..." : name;
    };

    return (
        <>
            <div className={candidatesStyles.profileImgContainer} >
                {item?.profile_image ?
                    <img src={process.env.REACT_APP_UPLOADS_PATH + item?.profile_image} alt="User avatar"
                        className={candidatesStyles.profileImg}
                        onError={(e) => {
                            e.target.onerror = null; // Prevent infinite loop
                            e.target.src = userAvatarPlaceholder; // Set fallback image
                        }}
                    />
                    :
                    <img src={userAvatarPlaceholder} className={candidatesStyles.profileImg} onError={(e) => {
                        e.target.onerror = null; // Prevent infinite loop
                        e.target.src = userAvatarPlaceholder; // Set fallback image
                    }} />
                }
            </div>
            <div className={candidatesStyles.badgeWrapper}>
                {item &&
                    <>
                        <Typography
                            title={truncateName(item?.first_name + " " + item?.last_name)}
                            weight={600}
                            color={'#0464B2'}
                            textAlignCenter="center"
                            fontSize={14}
                        />
                        {item?.verification_status && <img src={VerifyGreenBadge} className={candidatesStyles.verifyBadge} />}
                    </>
                }
            </div>
        </>
    )
};

// Component for rendering table cell content
const TableCellContent = ({ index, item, listOptions, onOpenCreateList, favTalents }) => {
    const renderContent = (content) => {
        // If content is not available, display "No Data"
        if (!content) {
            return (
                <Typography
                    title={t('general.UI.no_data')} // Use translation for "No Data"
                    color="#747474"
                    fontSize={14}
                    textAlignCenter="center"
                />
            );
        }
        return content;
    };

    const getListNameFromId = (listId) => {
        if (!listOptions || !listId) return null;
        const list = listOptions.find(list => list.id === listId);
        return list ? list.label : null;
    };

    // Get all lists for the user
    const getUserLists = () => {
        // Create a Set to store unique list names
        const listNames = new Set();

        // Check directly on the item first
        if (item) {
            // Check for lists array (our primary source of list data now)
            if (item.lists && Array.isArray(item.lists)) {
                item.lists.forEach(list => {
                    if (list.list_name) {
                        listNames.add(list.list_name);
                    } else if (list.list_id && listOptions) {
                        const listName = listOptions.find(opt => opt.id === list.list_id)?.label;
                        if (listName) listNames.add(listName);
                    }
                });
            }

            // Check for list_ids array
            if (item.list_ids && Array.isArray(item.list_ids)) {
                item.list_ids.forEach(id => {
                    const listName = listOptions?.find(list => list.id === id)?.label;
                    if (listName) listNames.add(listName);
                });
            }

            // Check for direct list_id and list_name
            if (item.list_id && listOptions) {
                const listName = listOptions.find(list => list.id === item.list_id)?.label;
                if (listName) listNames.add(listName);
            }

            if (item.list_name) {
                listNames.add(item.list_name);
            }

            // Check for favoriteList array
            if (item.favoriteList && Array.isArray(item.favoriteList)) {
                item.favoriteList.forEach(list => {
                    if (list.list_name) listNames.add(list.list_name);
                });
            }
        }

        // Now check in favTalents for additional information (as a fallback)
        if (favTalents && favTalents.length > 0 && item && item.id) {
            // Find all entries in favTalents that match this talent
            const relevantTalents = favTalents.filter(
                ft => (ft.talent_id === item.id || ft.id === item.id)
            );

            // Extract list information from each relevant talent entry
            relevantTalents.forEach(talent => {
                // Check for direct list_name
                if (talent.list_name) {
                    listNames.add(talent.list_name);
                }

                // Check for list_id with matching listOption
                if (talent.list_id && listOptions) {
                    const listName = listOptions.find(list => list.id === talent.list_id)?.label;
                    if (listName) listNames.add(listName);
                }

                // Check for lists array
                if (talent.lists && Array.isArray(talent.lists)) {
                    talent.lists.forEach(list => {
                        if (list.list_name) {
                            listNames.add(list.list_name);
                        } else if (list.list_id && listOptions) {
                            const listName = listOptions.find(opt => opt.id === list.list_id)?.label;
                            if (listName) listNames.add(listName);
                        }
                    });
                }
            });
        }

        // Convert the Set to an Array and return
        return Array.from(listNames);
    };

    // Component for Assign with Icon
    const AssignWithIcon = () => (
        <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', cursor: 'pointer' }}
            onClick={(e) => {
                e.stopPropagation();
                onOpenCreateList();
            }}
        >
            <Typography
                title={t('general.UI.assign')}
                color="#747474"
                fontSize={14}
                textAlignCenter="center"
            />
            <FaPlus size={12} color="#747474" />
        </div>
    );

    switch (index) {
        case 2:
            return renderContent(
                item?.country && (
                    <Typography
                        title={`${t('general.country.' + item?.country)} ${item?.city ? ', ' + t('general.city.' + item?.city.toLowerCase()) : ''}`}
                        color="#747474"
                        fontSize={14}
                        textAlignCenter="center"
                    />
                )
            );
        case 3:
            const userLists = getUserLists();
            console.log("User lists for item", item?.id, ":", userLists);
            let listDisplayText = '';

            if (userLists.length === 0) {
                // No lists - will show the Assign button
                listDisplayText = '';
            } else if (userLists.length === 1) {
                // Just one list - show it directly
                listDisplayText = userLists[0];
            } else if (userLists.length === 2) {
                // Two lists - show both with a comma
                listDisplayText = `${userLists[0]}, ${userLists[1]}`;
            } else {
                // More than two lists - show first two and "and X more..."
                const remainingCount = userLists.length - 2;
                listDisplayText = `${userLists[0]}, ${userLists[1]} ${t('general.UI.and')} ${remainingCount} ${t('general.UI.more')}...`;
            }

            return renderContent(
                userLists.length > 0 ? (
                    <div
                        style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpenCreateList();
                        }}
                    >
                        <span style={{ maxWidth: '70%' }}>
                            <Typography
                                title={listDisplayText}
                                color="#747474"
                                fontSize={14}
                            // textAlignCenter="center"
                            />
                        </span>
                        <FaPlus size={12} color="#747474" />
                    </div>
                ) : (
                    <AssignWithIcon />
                )
            );
        case 4:
            return renderContent(
                item?.working_title && (
                    <Typography title={item?.working_title} color="#747474" fontSize={14} textAlignCenter="center" />
                )
            );
        case 5:
            return renderContent(
                item?.gender && (
                    <Typography title={item?.gender} color="#747474" fontSize={14} textAlignCenter="center" />
                )
            );
        case 6:
            return renderContent(
                item?.last_visit && (
                    <Typography
                        title={getLastActiveTime(item?.last_visit)}
                        color={getLastActiveTime(item?.last_visit) === t('general.UI.online') ? 'green' : '#747474'}
                        fontSize={14}
                        textAlignCenter="center"
                    />
                )
            );
        default:
            return renderContent(
                <Typography title="item" color="#747474" fontSize={14} textAlignCenter="center" />
            );
    }
};

// Dropdown Component
const Dropdown = ({ onRemove, onViewProfile }) => {
    return (
        <div className={candidatesStyles.dropdown}>
            <div className={candidatesStyles.dropdownItem} onClick={onViewProfile}>
                {t('AboutCompany.viewProfile')}
            </div>
            <div className={candidatesStyles.dropdownItem} onClick={onRemove}>
                {t('general.UI.remove')}
            </div>
        </div>
    );
};

// Main TableHeaderUI component
export const TableHeaderUI = ({
    item,
    index,
    tableHeader,
    selectAll,
    handleSelectAll,
    isContent,
    listContent,
    isSelected,
    handleCandidateSelection,
    handleRemoveCandidate,
    listOptions,
    refreshJobSeekers,
    favTalents
}) => {
    const [dropdownEnabled, setDropdownEnabled] = useState(false);
    const [createListOpen, setCreateListOpen] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const user = useSelector(state => state.user.user);

    const toggleDropdown = () => {
        setDropdownEnabled(!dropdownEnabled);
    };

    const handleOpenCreateList = () => {
        setCreateListOpen(true);
    };

    const handleCloseCreateList = () => {
        setCreateListOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownEnabled(false);
            }
        };
        if (dropdownEnabled) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownEnabled]);

    // Table cell content with no loading state since we're using props
    const getTableCellContent = (cellIndex) => {
        return (
            <TableCellContent
                index={cellIndex}
                item={item}
                listOptions={listOptions}
                onOpenCreateList={handleOpenCreateList}
                favTalents={favTalents}
            />
        );
    };

    return (
        <>
            {isContent ? (
                <>
                    {dropdownEnabled && (
                        <div ref={dropdownRef}>
                            <Dropdown
                                onViewProfile={() => {
                                    navigate('/' + "jobseeker" + 's/' + item?.slug);
                                }}
                                onRemove={() => {
                                    handleRemoveCandidate(item?.application);
                                    setDropdownEnabled(false);
                                }}
                            />
                        </div>
                    )}

                    {createListOpen && (
                        <BasicPopup
                            isOpen={createListOpen}
                            closePopup={handleCloseCreateList}
                        >
                            <CreateFavListForm
                                userId={user.id}
                                favTalents={favTalents}
                                setFavTalents={newFavTalents => {
                                    // Ensure refresh happens after modal closes
                                    if (refreshJobSeekers) {
                                        // Increase timeout to ensure API has time to update
                                        setTimeout(refreshJobSeekers, 1000);
                                    }
                                }}
                                talentId={item?.id}
                                page={1}
                                per_page={1000}
                                filters={{}}
                                onclose={() => {
                                    handleCloseCreateList();
                                    if (refreshJobSeekers) {
                                        // Increase timeout to ensure API has time to update
                                        setTimeout(refreshJobSeekers, 1000);
                                    }
                                }}
                                onSubmit={(val) => {
                                    console.log("List form submitted:", val);
                                    if (refreshJobSeekers) {
                                        // Increase timeout to ensure API has time to update
                                        setTimeout(refreshJobSeekers, 1000);
                                    }
                                }}
                            />
                        </BasicPopup>
                    )}

                    {index === 0 ? (
                        <div
                            key={index.toString()}
                            onClick={handleCandidateSelection}
                            className={`${styles.tableHeading} ${styles.halfContainer} ${candidatesStyles.fullHeightCenterContent}`}
                            style={{ alignItems: "center", flex: '0.2' }}
                        >
                            <input
                                type="checkbox"
                                className={styles.customCheckbox}
                                checked={isSelected}
                                onChange={handleCandidateSelection}
                            />
                        </div>
                    ) : index === 1 ? (
                        <div
                            key={index.toString()}
                            onClick={handleCandidateSelection}
                            className={`${styles.tableHeading} ${styles.contentWrapper} ${candidatesStyles.profileContainer} ${candidatesStyles.fullHeightCenterContent}`}
                            // style={{ flex: '0' }}
                        >
                            <ProfileInfo item={item} />
                        </div>
                    ) : index === tableHeader.length - 1 ? (
                        listContent ? (
                            <div
                                onClick={toggleDropdown}
                                key={index.toString()}
                                className={`${styles.tableHeading} ${styles.emptyWrapper} ${candidatesStyles.fullHeightCenterContent}`}
                                style={{ alignItems: "center" }}
                            >
                                <TbDotsVertical style={{ cursor: 'pointer' }} />
                            </div>
                        ) : (
                            <div
                                onClick={handleCandidateSelection}
                                key={index.toString()}
                                className={`${styles.tableHeading} ${styles.emptyWrapper} ${candidatesStyles.fullHeightCenterContent}`}
                            />
                        )
                    ) : (
                        <div
                            onClick={handleCandidateSelection}
                            key={index.toString()}
                            className={`${styles.tableHeading} ${styles.contentWrapper} ${candidatesStyles.fullHeightCenterContent}`}
                            style={{ justifyContent: 'center', alignItems: 'center' }}
                        >
                            {getTableCellContent(index)}
                        </div>
                    )}
                </>
            ) : (
                <>
                    {index === 0 ? (
                        <div key={index.toString()} className={`${styles.tableHeading} ${styles.halfContainer}`} style={{ flex: '0.2' }}>
                            <input
                                type="checkbox"
                                className={styles.customCheckbox}
                                checked={selectAll}
                                onChange={handleSelectAll}
                            />
                        </div>
                    ) : index === tableHeader.length - 1 ? (
                        <div key={index.toString()} className={`${styles.tableHeading} ${styles.emptyWrapper}`} />
                    )

                        : (
                            <div key={index.toString()} className={`${styles.tableHeading} ${styles.contentWrapper}`} style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {item}
                            </div>
                        )
                    }
                </>
            )}
        </>
    );
};